import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { Grid, Paper, Container } from '@material-ui/core';
import Layout from 'components/Layout';
import Title from 'components/Title';
import Wizard from 'components/UI/Wizard';
import i18n from 'i18n-js';
import moment from 'moment';
import { Formik, Form } from 'formik';
import { buildUrl, removeEmpty, parseInputNumber } from 'utils';
import { useStyles } from './PromoCodeCreate.styled';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  CreditTypeInputs,
  BasicInformationInputs,
  initialValues,
  formSchema,
} from 'components/PromoCodes';
import { promoCodesActions } from 'state/modules/promoCodes';
import { FETCH_STATE } from 'constants';

const PromoCodeCreate = ({ clone = false }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const history = useHistory();
  const { id } = useParams();
  const validationSchema = useMemo(() => formSchema(), []);

  const {
    promoCodes: { promoCode, createPromoCode, loading },
  } = useSelector((state) => ({
    promoCodes: state.promoCodes,
  }));
  const [position, setPosition] = useState(0);

  const redirect = createPromoCode === FETCH_STATE.DONE;

  const stepsList = [
    {
      position: 0,
      key: 'credit',
      label: i18n.t('promo_codes.credit'),
    },
    {
      position: 1,
      key: 'basic-information',
      label: i18n.t('promo_codes.basic_information'),
    },
  ];

  const onSubmit = async (params) => {
    let payload = removeEmpty({
      ...params,
      ...(params.amount ? { amount: parseInputNumber(params.amount) } : {}),
    });

    if (payload.start_time) {
      payload.start_time = moment(payload.start_time)
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .format();
    }

    if (payload.expiration_time) {
      payload.expiration_time = moment(payload.expiration_time)
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .format();
    }
    dispatch(promoCodesActions.createPromoCode(payload));
  };

  const handleSave = () => {
    formRef.current.handleSubmit();
  };

  const handleCancel = () => {
    history.push(buildUrl('/promo-codes'));
  };

  const getTitle = () => {
    if (clone) {
      return i18n.t('promo_codes.clone');
    }
    return i18n.t('promo_codes.create');
  };

  const handleOnNext = () => {
    // We check validation credit type value on next button
    if (!formRef.current.values.invoice_item_type_id) {
      formRef.current.setFieldTouched('invoice_item_type_id', true);
      formRef.current.setErrors({ invoice_item_type_id: i18n.t('common.required_field') });
    } else {
      //We clear errors for the next step
      formRef.current.setErrors({});
      setPosition((currPosition) => {
        if (currPosition + 1 < stepsList.length) {
          window.scrollTo(0, 0);
          return currPosition + 1;
        } else {
          return currPosition;
        }
      });
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(promoCodesActions.fetchPromoCodeById(id));
    }
  }, [id]);

  const handleOnPrevious = () => {
    window.scrollTo(0, 0);
    if (position === 0) {
      history.push(buildUrl('/promo-codes'));
    } else {
      setPosition((currPosition) => currPosition - 1);
    }
  };

  useEffect(() => {
    if (redirect) {
      dispatch(promoCodesActions.resetPromoCode());
      history.push(buildUrl('/promo-codes'));
    }
  }, [redirect]);

  return (
    <Layout title={getTitle()}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={getTitle()} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginTop: '1.5rem' }}>
            <Formik
              initialValues={initialValues(id && promoCode, clone)}
              validationSchema={validationSchema}
              enableReinitialize={true}
              innerRef={formRef}
              onSubmit={onSubmit}
              //We only validate on change if errors are present
              validateOnChange={Boolean(Object.keys(formRef?.current?.errors || {}).length)}
              validateOnBlur={false}
            >
              {({ handleSubmit, errors, values }) => {
                return (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Paper elevation={2}>
                      <Wizard
                        exit={stepsList.length - 1 === position}
                        steps={stepsList}
                        loading={loading}
                        title={i18n.t('promo_codes.promo_code')}
                        position={position}
                        handleExit={handleSave}
                        onNext={handleOnNext}
                        onPrevious={handleOnPrevious}
                        handleEndOnboarding={handleCancel}
                        customStepperClass={classes.stepperClass}
                        customStepperRootClass={classes.customStepperRoot}
                        showPrevious
                      >
                        {stepsList[position].key === 'credit' && (
                          <>
                            <div className={classes.stepHeader}>
                              {i18n.t('promo_codes.credit_type_info')}
                            </div>
                            <CreditTypeInputs errors={errors} />
                          </>
                        )}
                        {stepsList[position].key === 'basic-information' && (
                          <>
                            <div className={classes.stepHeader}>
                              {i18n.t('promo_codes.basic_info_info')}
                            </div>
                            <BasicInformationInputs errors={errors} values={values} />
                          </>
                        )}
                      </Wizard>
                    </Paper>
                  </Form>
                );
              }}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export { PromoCodeCreate };
